/**
 * FAQSection — drop-in collapsible FAQ block for any page.
 * ===========================================================
 * Brand-aligned (slate-950 / emerald-300 accents, soft amber for warnings).
 * Pure presentation — content comes from `data/faqs.js`.
 *
 * Usage:
 *   import FAQSection from '../components/FAQSection';
 *   import { faqsByPage } from '../data/faqs';
 *   <FAQSection items={faqsByPage.pricing} />
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, HelpCircle, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQSection = ({
  title = 'Frequently asked questions',
  subtitle = '',
  items = [],
  testIdPrefix = 'faq',
  defaultOpenIndex = -1,
  variant = 'dark', // 'dark' | 'card' (dark subdued) | 'light' (for white-background pages)
}) => {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex);

  if (!items?.length) return null;

  const isLight = variant === 'light';

  const tokens = isLight
    ? {
        section: '',
        iconWrap: 'bg-emerald-100 border border-emerald-200',
        icon: 'text-emerald-700',
        title: 'text-slate-900',
        subtitle: 'text-slate-600',
        listWrap: 'divide-y divide-slate-200 border border-slate-200 rounded-xl bg-white overflow-hidden shadow-sm',
        triggerHover: 'hover:bg-emerald-50',
        triggerText: 'text-slate-900 group-hover:text-emerald-700',
        chevron: 'text-slate-500 group-hover:text-emerald-700',
        chevronOpen: 'text-emerald-700',
        answer: 'text-slate-700 border-l-2 border-emerald-400',
        link: 'text-emerald-700 hover:text-emerald-800',
        footnote: 'text-slate-600',
        footnoteLink: 'text-emerald-700 hover:text-emerald-800',
        seeAll: 'text-slate-600 hover:text-slate-900',
      }
    : {
        section: variant === 'card' ? 'bg-slate-900 border border-white/10 rounded-xl p-6' : '',
        iconWrap: 'bg-emerald-500/10 border border-emerald-400/30',
        icon: 'text-emerald-300',
        title: 'text-white',
        subtitle: 'text-slate-400',
        listWrap: 'divide-y divide-white/5 border border-white/10 rounded-xl bg-slate-950 overflow-hidden',
        triggerHover: 'hover:bg-emerald-500/5',
        triggerText: 'text-white group-hover:text-emerald-100',
        chevron: 'text-slate-400 group-hover:text-emerald-300',
        chevronOpen: 'text-emerald-300',
        answer: 'text-slate-400 border-l-2 border-emerald-500/30',
        link: 'text-emerald-300 hover:text-emerald-200',
        footnote: 'text-slate-400',
        footnoteLink: 'text-emerald-300 hover:text-emerald-200',
        seeAll: 'text-slate-400 hover:text-white',
      };

  return (
    <section
      className={tokens.section}
      data-testid={`${testIdPrefix}-section`}
      aria-label={title}
    >
      <div className="flex items-start gap-3 mb-6">
        <div className={`w-9 h-9 rounded-full ${tokens.iconWrap} flex items-center justify-center flex-shrink-0`}>
          <HelpCircle className={`w-4 h-4 ${tokens.icon}`} />
        </div>
        <div>
          <h2 className={`text-2xl sm:text-3xl font-bold ${tokens.title} tracking-tight`}>
            {title}
          </h2>
          {subtitle && (
            <p className={`${tokens.subtitle} text-sm mt-1.5 max-w-2xl`}>{subtitle}</p>
          )}
        </div>
      </div>

      <div className={tokens.listWrap}>
        {items.map((item, idx) => {
          const open = openIndex === idx;
          const id = item.id || `q-${idx}`;
          return (
            <div key={id} data-testid={`${testIdPrefix}-item-${id}`}>
              <button
                type="button"
                aria-expanded={open}
                aria-controls={`${testIdPrefix}-${id}-panel`}
                onClick={() => {
                  const willOpen = !open;
                  setOpenIndex(open ? -1 : idx);
                  if (willOpen && item.id) {
                    // Fire-and-forget click telemetry — feeds TopAskedFaqsWidget.
                    // Never block the user; swallow any network errors silently.
                    axios
                      .post(`${API}/faq/track-click`, {
                        faq_id: item.id,
                        source_page: testIdPrefix,
                      })
                      .catch(() => {});
                  }
                }}
                className={`w-full flex items-center justify-between gap-4 text-left px-5 py-4 ${tokens.triggerHover} transition-colors group`}
                data-testid={`${testIdPrefix}-trigger-${id}`}
              >
                <span className={`${tokens.triggerText} font-medium text-sm sm:text-base`}>
                  {item.q}
                </span>
                <ChevronDown
                  className={`w-4 h-4 flex-shrink-0 ${tokens.chevron} transition-transform ${
                    open ? `rotate-180 ${tokens.chevronOpen}` : ''
                  }`}
                />
              </button>
              <div
                id={`${testIdPrefix}-${id}-panel`}
                role="region"
                aria-hidden={!open}
                className={`overflow-hidden transition-all duration-200 ease-out ${
                  open ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className={`px-5 pb-5 -mt-1 ${tokens.answer} text-sm leading-relaxed ml-5`}>
                  <p className="pl-3" data-testid={`${testIdPrefix}-answer-${id}`}>{item.a}</p>
                  {item.links?.length > 0 && (
                    <div className="pl-3 pt-3 flex flex-wrap gap-3">
                      {item.links.map((l) => (
                        l.href.startsWith('/') && !l.href.startsWith('/api') ? (
                          <Link
                            key={l.href}
                            to={l.href}
                            className={`inline-flex items-center gap-1.5 ${tokens.link} text-xs font-medium`}
                          >
                            {l.label} <ExternalLink className="w-3 h-3" />
                          </Link>
                        ) : (
                          <a
                            key={l.href}
                            href={l.href}
                            className={`inline-flex items-center gap-1.5 ${tokens.link} text-xs font-medium`}
                          >
                            {l.label} <ExternalLink className="w-3 h-3" />
                          </a>
                        )
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
        <p className={`text-xs ${tokens.footnote}`}>
          Still have questions? Email{' '}
          <a href="mailto:hello@cartieai.com" className={tokens.footnoteLink}>
            hello@cartieai.com
          </a>{' '}
          — replies within 4 business hours.
        </p>
        <Link
          to="/faq"
          className={`text-xs ${tokens.seeAll} inline-flex items-center gap-1.5`}
          data-testid={`${testIdPrefix}-see-all`}
        >
          See full FAQ <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </section>
  );
};

export default FAQSection;
